import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'reduxTyped'

import { fetchFieldValues } from 'common/filter/actions/filterFieldValues'
import * as guards from 'common/filter/guards'
import { FilterFieldValuesState } from 'common/filter/reducers/interfaces'
import { getFilterFieldValues } from 'common/filter/selectors/filterFieldValues'
import {
  FilterFieldValueField,
  FilterFieldValueSegment,
  FilterFieldValue,
  FilterFieldValuePrimitive,
} from 'shared/api/requests/audience/schemas'
import { useDebounce } from 'utils/commonHooks'

const typeGuards = {
  ['all']: guards.isFilterFieldValue,
  ['segment']: guards.isFilterFieldValueSegment,
  ['default']: guards.isFilterFieldValueField,
  ['primitive']: guards.isFilterFieldValuePrimitive,
} as const

type TypeOfGuard<T> = T extends (value: unknown) => value is infer U ? U : never

type FilterFieldValueType = 'all' | 'segment' | 'default' | 'primitive'

interface UseFilterFieldValuesReturnType<T extends FilterFieldValue = FilterFieldValue>
  extends FilterFieldValuesState {
  items: T[]
  fetch: (options: Partial<UseFilterFieldValuesOptions>) => void
}

interface UseFilterFieldValuesOptions {
  fieldId: string
  query?: string
}

export function useFilterFieldValues(
  options: UseFilterFieldValuesOptions & { type?: 'all' },
): UseFilterFieldValuesReturnType
export function useFilterFieldValues(
  options: UseFilterFieldValuesOptions & { type: 'segment' },
): UseFilterFieldValuesReturnType<FilterFieldValueSegment>
export function useFilterFieldValues(
  options: UseFilterFieldValuesOptions & { type: 'default' },
): UseFilterFieldValuesReturnType<FilterFieldValueField>
export function useFilterFieldValues(
  options: UseFilterFieldValuesOptions & { type: 'primitive' },
): UseFilterFieldValuesReturnType<FilterFieldValuePrimitive>
export function useFilterFieldValues({
  type = 'all',
  ...initialOptions
}: UseFilterFieldValuesOptions & { type?: FilterFieldValueType }) {
  const dispatch = useAppDispatch()
  const [options, setOptions] = useState(initialOptions)
  const state = useAppSelector(getFilterFieldValues)
  // @TOOD move back filtering here with the typeGuards
  const items = useMemo(
    () => state.items as Array<TypeOfGuard<typeof typeGuards[typeof type]>>,
    [state.items],
  )

  const fetch = useCallback((params: Partial<UseFilterFieldValuesOptions> = {}) => {
    setOptions((current) => ({
      ...current,
      ...params,
    }))
  }, [])

  const delay = 150
  const { fieldId, query = '' } = useDebounce(options, delay)
  useEffect(() => {
    dispatch(
      fetchFieldValues({
        field: fieldId,
        query,
      }),
    )
  }, [fieldId, query, dispatch])

  return {
    ...state,
    items,
    fetch,
  } as UseFilterFieldValuesReturnType<TypeOfGuard<typeof typeGuards[typeof type]>>
}
